/**
 * Renders the small subset of Markdown the assistant writes — paragraphs,
 * headings, bullet and numbered lists, tables, **bold** and `code` — as React
 * elements. No HTML is ever injected, so model output can't smuggle markup
 * into the page.
 */
import { Fragment, ReactNode } from 'react';

function inline(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={i} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-neutral-700 text-[0.85em]">{part.slice(1, -1)}</code>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function cells(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
}

export default function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    if (line.trim().startsWith('|')) {
      const rows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) rows.push(lines[i++]);
      const [head, ...rest] = rows;
      const body = rest.filter(r => !/^\s*\|?\s*:?-{2,}/.test(r));
      blocks.push(
        <div key={blocks.length} className="overflow-x-auto my-2">
          <table className="min-w-full text-sm border border-gray-200 dark:border-neutral-700">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr>{cells(head).map((c, j) => <th key={j} className="px-2 py-1 text-left font-medium border-b border-gray-200 dark:border-neutral-700">{inline(c)}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((r, k) => (
                <tr key={k} className="border-b border-gray-100 dark:border-neutral-800">
                  {cells(r).map((c, j) => <td key={j} className="px-2 py-1 align-top">{inline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(<p key={blocks.length} className="font-semibold mt-3 mb-1">{inline(heading[2])}</p>);
      i++;
      continue;
    }

    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i++].replace(/^\s*([-*]|\d+\.)\s+/, ''));
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List key={blocks.length} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 my-1 space-y-0.5`}>
          {items.map((it, k) => <li key={k}>{inline(it)}</li>)}
        </List>
      );
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !lines[i].trim().startsWith('|') && !/^(#{1,4})\s/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
      para.push(lines[i++]);
    }
    blocks.push(<p key={blocks.length} className="my-1">{inline(para.join(' '))}</p>);
  }

  return <div className="text-sm leading-relaxed">{blocks}</div>;
}
